import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  BookPlus,
  ChevronLeft,
  Loader2,
  Trash2,
  Upload,
  Pencil,
  X,
  Save,
  Share2,
  ImageIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  fetchAdminEbooks,
  uploadEbook,
  deleteEbook,
  updateEbook,
  setEbookLeadMagnet,
  clearEbookLeadMagnet,
  fetchLeadMagnetStats,
  generateEbookCover,
  backfillEbookCovers,
  ebookCoverUrl,
} from "@/lib/api";
import UserBadge from "@/components/UserBadge";
import AdminToggle from "@/components/AdminToggle";
import SocialKitModal from "@/components/SocialKitModal";

const dropDate = (d) => (d ? new Date(d).toLocaleDateString() : "—");

export default function AdminEbooks() {
  const [ebooks, setEbooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [editing, setEditing] = useState(null); // the ebook being edited
  const [socialKitFor, setSocialKitFor] = useState(null); // ebook whose kit is open
  const [leadStats, setLeadStats] = useState(null);
  const [coverBusy, setCoverBusy] = useState({});
  const [backfillBusy, setBackfillBusy] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const [r, s] = await Promise.all([
        fetchAdminEbooks(),
        fetchLeadMagnetStats().catch(() => null),
      ]);
      setEbooks(r.ebooks || []);
      setLeadStats(s);
    } catch {
      toast.error("Could not load ebooks");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const remove = async (id) => {
    if (!confirm("Soft-delete this ebook? Users will no longer see it.")) return;
    try {
      await deleteEbook(id);
      toast.success("Deleted");
      await load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const toggleLeadMagnet = async (b) => {
    try {
      if (b.is_lead_magnet) {
        await clearEbookLeadMagnet(b.id);
        toast.success("Removed from free download");
      } else {
        await setEbookLeadMagnet(b.id);
        toast.success(`"${b.title}" is now the free download`);
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Toggle failed");
    }
  };

  const generateCover = async (b) => {
    try {
      setCoverBusy((m) => ({ ...m, [b.id]: true }));
      await generateEbookCover(b.id);
      toast.success(`Cover painted for "${b.title}"`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cover generation failed");
    } finally {
      setCoverBusy((m) => ({ ...m, [b.id]: false }));
    }
  };

  const runBackfill = async () => {
    const missing = ebooks.filter((b) => !b.cover_path).length;
    if (!missing) {
      toast.info("Every ebook already has a cover.");
      return;
    }
    if (!confirm(`Generate covers for ${missing} ebook${missing === 1 ? "" : "s"} that don't have one? Uses Nano Banana + Emergent LLM key. Runs sequentially — ~15s per cover.`)) return;
    try {
      setBackfillBusy(true);
      const res = await backfillEbookCovers();
      toast.success(`Covers painted: ${res.processed} processed`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Backfill failed");
    } finally {
      setBackfillBusy(false);
    }
  };

  const grouped = ebooks.reduce((acc, b) => {
    const k = b.drop_date || "Undated";
    acc[k] = acc[k] || [];
    acc[k].push(b);
    return acc;
  }, {});

  return (
    <div className="min-h-screen">
      <header className="px-6 md:px-12 py-6 flex items-center justify-between border-b border-obsidian-600">
        <Link
          to="/admin"
          className="inline-flex items-center gap-2 font-sans text-xs uppercase tracking-[0.24em] text-bone-400 hover:text-bone-100"
        >
          <ChevronLeft className="h-3 w-3" /> Admin
        </Link>
        <div className="flex items-center gap-4">
          <AdminToggle />
          <UserBadge />
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 md:px-12 pt-12 pb-24">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <div>
            <p className="font-sans uppercase tracking-[0.28em] text-xs text-terracotta mb-2">
              Library
            </p>
            <h1 className="font-serif text-4xl text-bone-100">Ebook drops</h1>
            <p className="font-serif italic text-bone-400 mt-1">
              Upload companion PDFs / EPUBs, group by drop date, gate by tier.
            </p>
          </div>
          <Button
            onClick={() => setShowUpload(true)}
            data-testid="admin-upload-ebook-btn"
            className="rounded-none bg-terracotta hover:bg-terracotta-deep text-bone-100 font-sans uppercase tracking-wider text-xs px-6 py-5"
          >
            <BookPlus className="h-4 w-4 mr-2" /> Upload an ebook
          </Button>
        </div>

        <div className="flex items-center gap-3 mb-6 flex-wrap">
          <button
            onClick={runBackfill}
            disabled={backfillBusy || loading}
            data-testid="admin-ebook-cover-backfill"
            className="inline-flex items-center gap-2 font-sans text-xs uppercase tracking-[0.24em] text-bone-400 hover:text-terracotta disabled:opacity-40 border border-obsidian-600 px-4 py-2"
            title="Nano Banana paints a moody cover for every ebook that doesn't have one"
          >
            {backfillBusy ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <ImageIcon className="h-3 w-3" />
            )}
            Backfill covers · Nano Banana
          </button>
        </div>

        {loading ? (
          <div className="flex items-center gap-3 text-bone-400">
            <Loader2 className="h-4 w-4 animate-spin" /> loading
          </div>
        ) : ebooks.length === 0 ? (
          <p className="font-sans text-bone-500 text-sm p-6 border border-obsidian-600">
            No ebooks uploaded yet.
          </p>
        ) : (
          <div className="space-y-10" data-testid="admin-ebook-groups">
            {leadStats && leadStats.ebook && (
              <div
                className="border border-terracotta/40 bg-terracotta/5 p-5 flex items-center gap-4 justify-between flex-wrap"
                data-testid="admin-lead-magnet-stats"
              >
                <div className="min-w-0">
                  <p className="font-sans uppercase tracking-[0.24em] text-[0.6rem] text-terracotta mb-1">
                    Active free download · lead magnet
                  </p>
                  <p className="font-serif text-lg text-bone-100 truncate">
                    {leadStats.ebook.title}
                  </p>
                  <p className="font-sans text-xs text-bone-400 mt-1">
                    {leadStats.claims} claim{leadStats.claims === 1 ? "" : "s"} · {leadStats.downloads} download{leadStats.downloads === 1 ? "" : "s"}
                  </p>
                </div>
                <div className="flex items-center gap-4 flex-wrap">
                  <button
                    onClick={() => {
                      const full = ebooks.find((e) => e.id === leadStats.ebook.id);
                      if (full) setSocialKitFor(full);
                    }}
                    data-testid="admin-lead-magnet-social-kit"
                    className="inline-flex items-center gap-2 font-sans text-xs uppercase tracking-[0.24em] text-terracotta hover:underline"
                  >
                    <Share2 className="h-3 w-3" /> Open social kit
                  </button>
                  <Link
                    to="/free-ebook"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-sans text-xs uppercase tracking-[0.24em] text-terracotta hover:underline"
                  >
                    View public page →
                  </Link>
                </div>
              </div>
            )}

            {Object.entries(grouped)
              .sort((a, b) => (a[0] < b[0] ? 1 : -1))
              .map(([k, list]) => (
                <div key={k}>
                  <p className="font-sans uppercase tracking-[0.28em] text-[0.65rem] text-terracotta mb-4">
                    Drop · {k === "Undated" ? "Undated" : dropDate(k)}
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {list.map((b) => (
                      <div
                        key={b.id}
                        data-testid={`admin-ebook-row-${b.id}`}
                        className="border border-obsidian-600 bg-obsidian-800/30 p-5"
                      >
                        <div className="flex gap-4 mb-3">
                          <div className="w-20 h-28 shrink-0 bg-obsidian-900 border border-obsidian-600 overflow-hidden flex items-center justify-center">
                            {b.cover_path ? (
                              <img
                                src={ebookCoverUrl(b.id)}
                                alt={`Cover of ${b.title}`}
                                className="w-full h-full object-cover"
                                data-testid={`admin-ebook-cover-thumb-${b.id}`}
                              />
                            ) : (
                              <ImageIcon className="h-6 w-6 text-bone-500" aria-hidden="true" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1 min-w-0">
                                <p className="font-serif text-lg text-bone-100 truncate">
                                  {b.title}
                                </p>
                                {b.subtitle && (
                                  <p className="font-serif italic text-bone-400 text-sm truncate">
                                    {b.subtitle}
                                  </p>
                                )}
                              </div>
                              <div className="flex items-center gap-1 shrink-0">
                                <button
                                  onClick={() => setEditing(b)}
                                  data-testid={`admin-ebook-edit-${b.id}`}
                                  title="Edit"
                                  className="text-bone-500 hover:text-terracotta p-1"
                                >
                                  <Pencil className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => remove(b.id)}
                                  data-testid={`admin-ebook-delete-${b.id}`}
                                  title="Delete"
                                  className="text-bone-500 hover:text-red-400 p-1"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>
                            </div>
                            <p className="font-sans text-xs text-bone-500 mt-1">
                              {b.author || "Umbra"} · {(b.size_bytes / 1024).toFixed(0)} KB · .{b.ext}
                            </p>
                          </div>
                        </div>
                        <div className="flex gap-2 flex-wrap">
                          {b.is_lead_magnet && (
                            <span className="font-sans text-[0.55rem] uppercase tracking-[0.2em] text-terracotta border border-terracotta bg-terracotta/10 px-2 py-0.5">
                              Free download
                            </span>
                          )}
                          {b.free_tier && (
                            <span className="font-sans text-[0.55rem] uppercase tracking-[0.2em] text-terracotta border border-terracotta/60 px-2 py-0.5">
                              Free tier
                            </span>
                          )}
                          {b.published ? (
                            <span className="font-sans text-[0.55rem] uppercase tracking-[0.2em] text-emerald-400 border border-emerald-400/40 px-2 py-0.5">
                              Live
                            </span>
                          ) : (
                            <span className="font-sans text-[0.55rem] uppercase tracking-[0.2em] text-bone-500 border border-obsidian-600 px-2 py-0.5">
                              Draft
                            </span>
                          )}
                        </div>
                        <button
                          onClick={() => generateCover(b)}
                          disabled={coverBusy[b.id]}
                          data-testid={`admin-ebook-cover-generate-${b.id}`}
                          className={`mt-3 w-full font-sans uppercase tracking-[0.24em] text-[0.6rem] px-3 py-2 border transition-colors inline-flex items-center justify-center gap-2 ${
                            b.cover_path
                              ? "border-obsidian-600 text-bone-400 hover:border-terracotta hover:text-terracotta"
                              : "border-terracotta text-terracotta hover:bg-terracotta hover:text-obsidian-900"
                          }`}
                        >
                          {coverBusy[b.id] ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <ImageIcon className="h-3 w-3" />
                          )}
                          {b.cover_path ? "Regenerate cover" : "Paint cover · Nano Banana"}
                        </button>
                        <button
                          onClick={() => toggleLeadMagnet(b)}
                          data-testid={`admin-ebook-lead-toggle-${b.id}`}
                          className={`mt-2 w-full font-sans uppercase tracking-[0.24em] text-[0.6rem] px-3 py-2 border transition-colors ${
                            b.is_lead_magnet
                              ? "border-terracotta text-terracotta hover:bg-obsidian-900"
                              : "border-obsidian-600 text-bone-400 hover:border-terracotta hover:text-terracotta"
                          }`}
                        >
                          {b.is_lead_magnet ? "Remove as free download" : "Set as free download"}
                        </button>
                        {b.is_lead_magnet && (
                          <button
                            onClick={() => setSocialKitFor(b)}
                            data-testid={`admin-ebook-social-kit-${b.id}`}
                            className="mt-2 w-full font-sans uppercase tracking-[0.24em] text-[0.6rem] px-3 py-2 border border-obsidian-600 text-bone-300 hover:border-terracotta hover:text-terracotta transition-colors inline-flex items-center justify-center gap-2"
                          >
                            <Share2 className="h-3 w-3" /> Social post kit
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
          </div>
        )}

        {showUpload && (
          <UploadModal
            onClose={() => setShowUpload(false)}
            onSuccess={async () => {
              setShowUpload(false);
              await load();
            }}
          />
        )}
        {editing && (
          <EditModal
            ebook={editing}
            onClose={() => setEditing(null)}
            onSuccess={async () => {
              setEditing(null);
              await load();
            }}
          />
        )}
        {socialKitFor && (
          <SocialKitModal
            ebook={socialKitFor}
            onClose={() => setSocialKitFor(null)}
          />
        )}
      </div>
    </div>
  );
}

function EditModal({ ebook, onClose, onSuccess }) {
  const [form, setForm] = useState({
    title: ebook.title || "",
    subtitle: ebook.subtitle || "",
    description: ebook.description || "",
    published: !!ebook.published,
    free_tier: !!ebook.free_tier,
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    // Send only fields that changed — the PUT endpoint treats undefined as
    // "leave as-is" so this keeps updates minimal + auditable.
    const patch = new FormData();
    if (form.title !== ebook.title) patch.append("title", form.title);
    if (form.subtitle !== (ebook.subtitle || "")) patch.append("subtitle", form.subtitle);
    if (form.description !== (ebook.description || "")) patch.append("description", form.description);
    if (form.published !== !!ebook.published) patch.append("published", String(form.published));
    if (form.free_tier !== !!ebook.free_tier) patch.append("free_tier", String(form.free_tier));

    try {
      setBusy(true);
      await updateEbook(ebook.id, patch);
      toast.success("Ebook updated");
      onSuccess();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-obsidian-950/85 backdrop-blur-md flex items-center justify-center p-4"
      data-testid="admin-ebook-edit-modal"
    >
      <div className="relative bg-obsidian-800 border border-obsidian-600 max-w-xl w-full max-h-[90vh] overflow-y-auto p-8">
        <button
          onClick={onClose}
          className="absolute top-5 right-5 text-bone-500 hover:text-bone-200"
          data-testid="admin-ebook-edit-close"
        >
          <X className="h-5 w-5" />
        </button>
        <h2 className="font-serif text-2xl text-bone-100 mb-1">Edit ebook</h2>
        <p className="font-sans text-xs text-bone-500 mb-6 truncate">
          {ebook.id} · file .{ebook.ext} · {(ebook.size_bytes / 1024).toFixed(0)} KB
        </p>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="font-sans text-[0.65rem] uppercase tracking-[0.24em] text-bone-500 block mb-1">
              Title
            </label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              data-testid="admin-ebook-edit-title"
              className="w-full bg-obsidian-900 border border-obsidian-600 focus:border-terracotta text-bone-100 px-3 py-2 font-sans text-sm focus:outline-none"
            />
          </div>
          <div>
            <label className="font-sans text-[0.65rem] uppercase tracking-[0.24em] text-bone-500 block mb-1">
              Subtitle
            </label>
            <input
              type="text"
              value={form.subtitle}
              onChange={(e) => setForm({ ...form, subtitle: e.target.value })}
              data-testid="admin-ebook-edit-subtitle"
              className="w-full bg-obsidian-900 border border-obsidian-600 focus:border-terracotta text-bone-100 px-3 py-2 font-sans text-sm focus:outline-none"
            />
          </div>
          <div>
            <label className="font-sans text-[0.65rem] uppercase tracking-[0.24em] text-bone-500 block mb-1">
              Description
            </label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={4}
              data-testid="admin-ebook-edit-description"
              className="w-full bg-obsidian-900 border border-obsidian-600 focus:border-terracotta text-bone-100 px-3 py-2 font-sans text-sm focus:outline-none"
            />
          </div>
          <div className="flex gap-6 pt-2">
            <label className="flex items-center gap-2 font-sans text-xs text-bone-300">
              <input
                type="checkbox"
                checked={form.free_tier}
                onChange={(e) => setForm({ ...form, free_tier: e.target.checked })}
                data-testid="admin-ebook-edit-free-tier"
              />
              Free tier
            </label>
            <label className="flex items-center gap-2 font-sans text-xs text-bone-300">
              <input
                type="checkbox"
                checked={form.published}
                onChange={(e) => setForm({ ...form, published: e.target.checked })}
                data-testid="admin-ebook-edit-published"
              />
              Published
            </label>
          </div>
          <div className="flex items-center gap-3 pt-4">
            <Button
              type="submit"
              disabled={busy}
              data-testid="admin-ebook-edit-submit"
              className="rounded-none bg-terracotta hover:bg-terracotta-deep text-bone-100 font-sans uppercase tracking-wider text-xs py-5 px-8"
            >
              {busy ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Save changes
            </Button>
            <button
              type="button"
              onClick={onClose}
              data-testid="admin-ebook-edit-cancel"
              className="font-sans text-xs uppercase tracking-[0.24em] text-bone-400 hover:text-bone-100 border-b border-transparent hover:border-bone-400 pb-0.5"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function UploadModal({ onClose, onSuccess }) {
  const [form, setForm] = useState({
    title: "",
    subtitle: "",
    author: "Umbra",
    description: "",
    drop_date: "",
    tags: "",
    free_tier: false,
    published: true,
  });
  const [file, setFile] = useState(null);
  const [cover, setCover] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error("Choose an ebook file");
      return;
    }
    const fd = new FormData();
    for (const [k, v] of Object.entries(form)) fd.append(k, v);
    fd.append("file", file);
    if (cover) fd.append("cover", cover);
    try {
      setBusy(true);
      await uploadEbook(fd);
      toast.success("Uploaded");
      onSuccess();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-obsidian-950/85 backdrop-blur-md flex items-center justify-center p-4"
      data-testid="admin-ebook-upload-modal"
    >
      <div className="relative bg-obsidian-800 border border-obsidian-600 max-w-2xl w-full max-h-[90vh] overflow-y-auto p-8">
        <button
          onClick={onClose}
          className="absolute top-5 right-5 text-bone-500 hover:text-bone-200"
          data-testid="admin-ebook-upload-close"
        >
          <X className="h-5 w-5" />
        </button>
        <h2 className="font-serif text-2xl text-bone-100 mb-1">Upload an ebook</h2>
        <p className="font-sans text-xs text-bone-500 mb-6">
          PDF / EPUB up to 50 MB. Free-tier drops are downloadable by everyone.
        </p>
        <form onSubmit={submit} className="space-y-4">
          {[
            ["title", "Title", true],
            ["subtitle", "Subtitle", false],
            ["author", "Author", false],
            ["drop_date", "Drop date", false, "date"],
            ["tags", "Tags (comma sep)", false],
          ].map(([k, label, required, type = "text"]) => (
            <div key={k}>
              <label className="font-sans text-[0.65rem] uppercase tracking-[0.24em] text-bone-500 block mb-1">
                {label}
                {required && " *"}
              </label>
              <input
                type={type}
                required={required}
                value={form[k]}
                onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                data-testid={`admin-ebook-${k}`}
                className="w-full bg-obsidian-900 border border-obsidian-600 focus:border-terracotta text-bone-100 px-3 py-2 font-sans text-sm focus:outline-none"
              />
            </div>
          ))}
          <div>
            <label className="font-sans text-[0.65rem] uppercase tracking-[0.24em] text-bone-500 block mb-1">
              Description
            </label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={3}
              data-testid="admin-ebook-description"
              className="w-full bg-obsidian-900 border border-obsidian-600 focus:border-terracotta text-bone-100 px-3 py-2 font-sans text-sm focus:outline-none"
            />
          </div>
          <div className="flex gap-6">
            <label className="flex items-center gap-2 font-sans text-xs text-bone-300">
              <input
                type="checkbox"
                checked={form.free_tier}
                onChange={(e) => setForm({ ...form, free_tier: e.target.checked })}
                data-testid="admin-ebook-free-tier"
              />
              Free tier
            </label>
            <label className="flex items-center gap-2 font-sans text-xs text-bone-300">
              <input
                type="checkbox"
                checked={form.published}
                onChange={(e) => setForm({ ...form, published: e.target.checked })}
                data-testid="admin-ebook-published"
              />
              Published
            </label>
          </div>
          <div>
            <label className="font-sans text-[0.65rem] uppercase tracking-[0.24em] text-bone-500 block mb-1">
              Ebook file · PDF or EPUB *
            </label>
            <input
              type="file"
              accept=".pdf,.epub,.mobi"
              required
              onChange={(e) => setFile(e.target.files[0])}
              data-testid="admin-ebook-file"
              className="w-full font-sans text-sm text-bone-300 file:bg-obsidian-900 file:border file:border-obsidian-600 file:text-bone-100 file:px-3 file:py-2 file:mr-3 file:font-sans file:text-xs"
            />
          </div>
          <div>
            <label className="font-sans text-[0.65rem] uppercase tracking-[0.24em] text-bone-500 block mb-1">
              Cover image (optional)
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setCover(e.target.files[0])}
              data-testid="admin-ebook-cover"
              className="w-full font-sans text-sm text-bone-300 file:bg-obsidian-900 file:border file:border-obsidian-600 file:text-bone-100 file:px-3 file:py-2 file:mr-3 file:font-sans file:text-xs"
            />
          </div>
          <Button
            type="submit"
            disabled={busy}
            data-testid="admin-ebook-upload-submit"
            className="rounded-none bg-terracotta hover:bg-terracotta-deep text-bone-100 font-sans uppercase tracking-wider text-xs py-5 px-8 w-full"
          >
            {busy ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Upload className="h-4 w-4 mr-2" />
            )}
            Upload
          </Button>
        </form>
      </div>
    </div>
  );
}
